import React, { useState, useCallback, useEffect } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, TextField, Typography, CircularProgress, Chip, IconButton,
    Collapse, Tooltip, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useFirebase } from '../../context/FirebaseContext';
import { getDeletionHistoryV2, retryDeletionV2 } from '../../api/admin';
import logger from '../../utils/logger';
import './AdminView.css';

const UsersHistoryTab = () => {
    const { auth } = useFirebase();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchEmail, setSearchEmail] = useState('');
    const [expandedRows, setExpandedRows] = useState({});

    const loadHistory = useCallback(async (email = null) => {
        try {
            setLoading(true);
            const response = await new Promise((resolve) => {
                getDeletionHistoryV2(auth, resolve, email || null);
            });
            if (Array.isArray(response)) {
                setHistory(response);
            } else if (response.error) {
                logger.error('Error loading deletion history:', response.error);
            }
        } catch (error) {
            logger.error('Error loading deletion history:', error);
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const handleSearch = () => {
        loadHistory(searchEmail.trim() || null);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleRetry = async (id) => {
        const confirmed = window.confirm(
            'Are you sure you want to retry this deletion? This will attempt to delete any remaining user data.'
        );
        if (!confirmed) return;

        try {
            setLoading(true);
            const response = await new Promise((resolve) => {
                retryDeletionV2(auth, resolve, id);
            });
            if (response.error) {
                alert('Retry failed: ' + response.error);
            } else {
                alert('Retry completed successfully.');
                loadHistory(searchEmail.trim() || null);
            }
        } catch (error) {
            logger.error('Error retrying deletion:', error);
            alert('Failed to retry deletion.');
        } finally {
            setLoading(false);
        }
    };

    const toggleRow = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        if (Array.isArray(dateValue)) {
            const [year, month, day, hour, minute, second] = dateValue;
            return new Date(year, month - 1, day, hour, minute, second).toLocaleString();
        }
        return new Date(dateValue).toLocaleString();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'partial': return 'warning';
            case 'failed': return 'error';
            default: return 'default';
        }
    };

    if (loading && history.length === 0) {
        return (
            <Box className="admin-loading">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="admin-tab-content">
            {/* Search */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                    label="Search by email"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    sx={{ minWidth: { xs: 0, sm: 300 } }}
                />
                <Tooltip title="Refresh">
                    <IconButton onClick={() => handleSearch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Desktop Table View */}
            <Box className="admin-table-desktop">
                <TableContainer component={Paper} className="admin-table-container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width={40}></TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Deleted At</TableCell>
                                <TableCell>Deleted By</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Total Rows</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography color="textSecondary">
                                            No deletion history found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                history.map((record) => (
                                    <React.Fragment key={record.id}>
                                        <TableRow hover>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => toggleRow(record.id)}>
                                                    {expandedRows[record.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>{record.userEmail}</TableCell>
                                            <TableCell>{formatDate(record.deletedAt)}</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {record.deletedBy}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={record.status}
                                                    size="small"
                                                    color={getStatusColor(record.status)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {record.deletionDetails?.total_rows_deleted || 0}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedRows[record.id] ? undefined : 'none' }}>
                                                <Collapse in={expandedRows[record.id]} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            User Details
                                                        </Typography>
                                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
                                                            <Box>
                                                                <Typography variant="caption" color="textSecondary">User ID</Typography>
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                                    {record.userId}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="caption" color="textSecondary">Created At</Typography>
                                                                <Typography variant="body2">{formatDate(record.createdAt)}</Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="caption" color="textSecondary">Last Login</Typography>
                                                                <Typography variant="body2">{formatDate(record.lastLogin)}</Typography>
                                                            </Box>
                                                        </Box>

                                                        {record.deletionDetails?.tables && (
                                                            <>
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    Deleted Records by Table
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                                    {Object.entries(record.deletionDetails.tables).map(([table, count]) => (
                                                                        <Chip
                                                                            key={table}
                                                                            label={`${table}: ${count}`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </>
                                                        )}

                                                        {record.errorMessage && (
                                                            <Box sx={{ mt: 2 }}>
                                                                <Typography variant="subtitle2" color="error" gutterBottom>
                                                                    Error
                                                                </Typography>
                                                                <Typography variant="body2" color="error">
                                                                    {record.errorMessage}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {record.status !== 'completed' && (
                                                            <Box sx={{ mt: 2 }}>
                                                                <Chip
                                                                    label="Retry Deletion"
                                                                    color="warning"
                                                                    onClick={() => handleRetry(record.id)}
                                                                    sx={{ cursor: 'pointer' }}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Mobile Card View */}
            {isMobile && (
                <Box className="admin-cards-mobile">
                    {history.length === 0 ? (
                        <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                            No deletion history found
                        </Typography>
                    ) : (
                        history.map((record) => (
                            <Paper key={record.id} className="admin-mobile-card">
                                <Box className="admin-mobile-card-header">
                                    <Box>
                                        <span className="admin-mobile-card-title">{record.userEmail}</span>
                                    </Box>
                                    <Chip
                                        label={record.status}
                                        size="small"
                                        color={getStatusColor(record.status)}
                                    />
                                </Box>
                                <Box className="admin-mobile-card-body">
                                    <Box className="admin-mobile-card-row">
                                        <span className="admin-mobile-card-row-label">Deleted At</span>
                                        <span className="admin-mobile-card-row-value">{formatDate(record.deletedAt)}</span>
                                    </Box>
                                    <Box className="admin-mobile-card-row">
                                        <span className="admin-mobile-card-row-label">Deleted By</span>
                                        <span className="admin-mobile-card-row-value">{record.deletedBy}</span>
                                    </Box>
                                    <Box className="admin-mobile-card-row">
                                        <span className="admin-mobile-card-row-label">Total Rows Deleted</span>
                                        <span className="admin-mobile-card-row-value">
                                            {record.deletionDetails?.total_rows_deleted || 0}
                                        </span>
                                    </Box>
                                </Box>

                                {/* Expandable Details */}
                                <Box sx={{ mt: 1 }}>
                                    <Box
                                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                        onClick={() => toggleRow(record.id)}
                                    >
                                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                                            {expandedRows[record.id] ? 'Hide Details' : 'Show Details'}
                                        </Typography>
                                        <IconButton size="small">
                                            {expandedRows[record.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                        </IconButton>
                                    </Box>
                                    <Collapse in={expandedRows[record.id]} timeout="auto" unmountOnExit>
                                        <Box sx={{ pt: 1 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                User Details
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                                <Box>
                                                    <Typography variant="caption" color="textSecondary">User ID</Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                        {record.userId}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="textSecondary">Created At</Typography>
                                                    <Typography variant="body2">{formatDate(record.createdAt)}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="textSecondary">Last Login</Typography>
                                                    <Typography variant="body2">{formatDate(record.lastLogin)}</Typography>
                                                </Box>
                                            </Box>

                                            {record.deletionDetails?.tables && (
                                                <>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Deleted Records by Table
                                                    </Typography>
                                                    <Box className="admin-mobile-card-chips">
                                                        {Object.entries(record.deletionDetails.tables).map(([table, count]) => (
                                                            <Chip
                                                                key={table}
                                                                label={`${table}: ${count}`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Box>
                                                </>
                                            )}

                                            {record.errorMessage && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="subtitle2" color="error" gutterBottom>
                                                        Error
                                                    </Typography>
                                                    <Typography variant="body2" color="error" sx={{ wordBreak: 'break-word' }}>
                                                        {record.errorMessage}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Collapse>
                                </Box>

                                {record.status !== 'completed' && (
                                    <Box className="admin-mobile-card-actions">
                                        <Chip
                                            label="Retry Deletion"
                                            color="warning"
                                            onClick={() => handleRetry(record.id)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    </Box>
                                )}
                            </Paper>
                        ))
                    )}
                </Box>
            )}
        </Box>
    );
};

export default UsersHistoryTab;
